import { useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'

/** A change the server previewed, and the request that previewed it. */
export interface PreviewedChange<TRequest, TChange> {
  request: TRequest
  change: TChange
}

/** The preview-then-apply flow a billing change form runs: preview a request,
 *  show what the server says it does, and write only what was previewed. */
export function usePreviewedChange<
  TRequest extends { confirm: boolean },
  TChange,
>(
  mutation: UseMutationResult<TChange, Error, TRequest>,
  {
    writing,
    setWriting,
    onDone,
  }: {
    /** A write is in flight; held by the sheet, which stays open meanwhile. */
    writing: boolean
    setWriting: (writing: boolean) => void
    onDone: () => void
  }
) {
  const [preview, setPreview] = useState<PreviewedChange<
    TRequest,
    TChange
  > | null>(null)
  const [error, setError] = useState<string | null>(null)

  const busy = mutation.isPending || writing

  // Any edit invalidates the preview, so Apply change only writes what was
  // previewed.
  function edit(change: () => void) {
    change()
    setPreview(null)
    setError(null)
  }

  function requestPreview(request: TRequest) {
    setError(null)
    mutation.mutate(request, {
      onSuccess: (change) => setPreview({ request, change }),
      onError: (err) => setError(err.message),
    })
  }

  /** Writes `request`, the previewed one with whatever the confirmation
   *  names, with `confirm` set. */
  function apply(request: TRequest) {
    setWriting(true)
    setError(null)
    mutation.mutate(
      { ...request, confirm: true },
      {
        onSuccess: onDone,
        onError: (err) => {
          // The admin previews again before retrying: the failed write may
          // have landed or been undone, what is in force may differ from the
          // preview, and the month may have turned.
          setWriting(false)
          setPreview(null)
          setError(err.message)
        },
      }
    )
  }

  return { preview, error, busy, edit, requestPreview, apply }
}
